/**
 * Play Archive column registry — the ONE place a stat becomes a column.
 *
 * Every column describes itself once and that description drives the whole
 * page: the table cell, its sort key, its header filter (facets), its place
 * in the column picker, the row expander, and the fields of the edit / insert
 * dialog. Catalog stats are generated from `PLAY_ENTRY_STAT_CARDS` (the web
 * mirror of the shared stat-option catalog), so a stat added there — say a
 * new "Offensive motion" style stat — appears in the table, the filters, the
 * picker and the editor with zero page code. `archive-columns.test.ts` proves
 * the coverage.
 *
 * Catalog fields are phase-aware: `offensive_formation` is OUR formation on an
 * offensive snap and the OPPONENT's on a defensive one (the play-entry card
 * map says which side per phase). Cells mark the opponent value, and the edit
 * dialog files it under "Opponent look" with the card's own label.
 */
import type {
  PlayEntryStatCardView,
  PlayView,
  StatCategory,
  StatFieldDefinition,
  StatSide,
} from '../../lib/api-types';
import {
  CONCEPT_DESIGN_LABELS,
  CONCEPT_DESIGN_TYPES,
  DESIGNED_PLAY_TYPE_FIELD,
  isConceptDesignType,
} from '../../lib/concept-design';
import {
  FIELD_BOUNDS,
  HASH_OPTIONS,
  PHASE_LABELS,
  PHASE_SHORT_LABELS,
  PLAY_OUTCOME_LABELS,
  PLAY_PHASES,
  PLAY_TYPES_BY_PHASE,
  SPECIALTY_OUTCOME_OPTIONS,
  isPlayPhase,
  outcomeOptionsFor,
  type PlayPhase,
} from '../play-entry/play-catalog';
import { DEFENSIVE_PENALTY_TAG, OFFENSIVE_PENALTY_TAG } from '../play-entry/play-draft';
import {
  PLAY_DIRECTIONS,
  PLAY_DIRECTION_LABELS,
  PLAY_ENTRY_STAT_CARDS,
  statDefinition,
} from '../playbook/stat-catalog';

import { CUSTOM_COLUMN_PREFIX, formatCustomStatValue, NOT_RECORDED } from './custom-columns';

/* ------------------------------------------------------------------ types */

export interface ValueOption {
  value: string;
  label: string;
}

/** Enum filter value meaning "the field is blank on this play". */
export const BLANK_VALUE = '__blank__';
export const BLANK_LABEL = 'Not recorded';

export type FacetKind = 'enum' | 'range' | 'text';

/**
 * One filterable dimension of a column. Combined cells ("2nd & 5 · OWN 35")
 * carry several facets (down, distance, yard line), each filterable alone.
 */
export interface FilterFacet {
  /** URL key (`f.<id>`); unique across the registry. */
  id: string;
  label: string;
  kind: FacetKind;
  /** enum: the values this play contributes (empty = blank). */
  values?: (play: PlayView) => string[];
  /** range: the number this play contributes. */
  number?: (play: PlayView) => number | null;
  /** text: the text searched with "contains". */
  text?: (play: PlayView) => string;
  /** enum: human label for a raw value. */
  optionLabel?: (value: string) => string;
  /** enum: a fixed vocabulary (shown even when no play carries it). */
  fixedOptions?: readonly ValueOption[];
  /** range: sensible bounds for the inputs. */
  min?: number;
  max?: number;
}

export type EditSection = 'situation' | 'call' | 'look' | 'result' | 'tags' | 'custom';

export const EDIT_SECTIONS: readonly { id: EditSection; label: string }[] = [
  { id: 'situation', label: 'Situation' },
  { id: 'result', label: 'Result' },
  { id: 'call', label: 'Call' },
  { id: 'look', label: 'Opponent look' },
  { id: 'tags', label: 'Tags & notes' },
  { id: 'custom', label: 'Custom stats' },
];

export type EditKind = 'text' | 'number' | 'select' | 'multi' | 'textarea' | 'list';

/** One control in the edit / insert dialog. `name` is the wire field. */
export interface EditField {
  name: string;
  kind: EditKind;
  section: EditSection;
  phases: readonly PlayPhase[];
  label: (phase: PlayPhase) => string;
  /** select / multi: offered options for this phase (+ current value kept). */
  options?: (phase: PlayPhase, draft: Readonly<Record<string, string>>) => readonly ValueOption[];
  hint?: string;
  placeholder?: string;
  /** Lives under `custom_stats` on the wire. */
  customKey?: string;
  /** Grid span in the dialog (1–3 columns). */
  span?: 1 | 2 | 3;
  min?: number;
  max?: number;
  /**
   * A `multi` field whose values live in the play's `tags[]` (the Offensive /
   * Defensive Penalty chips), not under its own stat key. The free-form
   * `tags` field then shows only the remaining tags; the two are merged back
   * into one `tags` array on save.
   */
  storedInTags?: boolean;
}

export type CellKind =
  'text' | 'play' | 'phase' | 'gain' | 'tags' | 'success' | 'catalog' | 'situation';

export type ColumnGroup = 'situation' | 'call' | 'look' | 'result' | 'tags' | 'meta' | 'custom';

export const COLUMN_GROUP_LABELS: Readonly<Record<ColumnGroup, string>> = {
  situation: 'Situation',
  call: 'Call',
  look: 'Opponent look',
  result: 'Result',
  tags: 'Tags & notes',
  meta: 'Record',
  custom: 'Custom stats',
};

export interface ArchiveColumn {
  id: string;
  /** Compact header text. */
  header: string;
  /** Small line above the header (`Off.` / `Def.` / `ST`) for catalog stats. */
  kicker?: string;
  /** Full name (picker, filter popover, expander, tooltip). */
  title: string;
  group: ColumnGroup;
  /** Lower = more important: kept first when the table is narrow. */
  priority: number;
  /** Narrowest the column may render, in CSS px (at the 13 px design size). */
  minWidth: number;
  /**
   * Widest its measured content may claim before it ellipsizes (design px).
   * Defaults to 2 × minWidth; spare width from `grow` can still exceed it.
   */
  maxWidth?: number;
  /** Share of spare width (default 1). */
  grow: number;
  align: 'start' | 'end' | 'center';
  defaultVisible: boolean;
  /** Always shown, never hideable (Play #). */
  isPinned?: boolean;
  cell: CellKind;
  /** Phases the column means anything on (undefined = all). */
  phases?: readonly PlayPhase[];
  /** Plain text of the cell — tooltip, search, expander, phone card. */
  text: (play: PlayView) => string;
  sortValue: (play: PlayView) => string | number | null;
  facets: readonly FilterFacet[];
  editFields: readonly EditField[];
  /** Catalog columns: the side per phase (own / opponent). */
  sideByPhase?: Partial<Record<PlayPhase, StatSide>>;
  category?: StatCategory;
  /** Custom stat columns: the definition (retired flag, data type). */
  customField?: StatFieldDefinition;
}

/** What the registry needs from the page: the team's option vocabulary. */
export interface RegistryContext {
  /** Team-enabled options for a catalog stat on one side (Play Entry's list). */
  optionsFor: (category: StatCategory, side: StatSide) => readonly ValueOption[];
  /** Every custom stat field, retired ones included. */
  customFields: readonly StatFieldDefinition[];
}

/* -------------------------------------------------------------- formatting */

export function phaseOf(play: PlayView): PlayPhase | null {
  const phase = play.stats.possession;
  return isPlayPhase(phase) ? phase : null;
}

function str(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(String).join(', ');
  return String(value).trim();
}

export function num(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function list(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String).filter((entry) => entry.trim() !== '');
  if (typeof value === 'string' && value.trim() !== '') {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry !== '');
  }
  return [];
}

/** 1–99 toward the opponent's goal → `OWN 35` / `50` / `OPP 20`. */
export function formatYardLine(yard: number | null): string {
  if (yard === null) return '';
  if (yard < 50) return `OWN ${yard}`;
  if (yard === 50) return '50';
  return `OPP ${100 - yard}`;
}

export function ordinalDown(down: number): string {
  return ['', '1st', '2nd', '3rd', '4th'][down] ?? `${down}th`;
}

/** Yards to go: explicit `distance`, else first-down line − LOS. */
export function distanceOf(stats: Record<string, unknown>): number | null {
  const explicit = num(stats.distance);
  if (explicit !== null) return explicit;
  const los = num(stats.line_of_scrimmage);
  const marker = num(stats.first_down_line);
  if (los === null || marker === null) return null;
  return marker - los;
}

/** `2nd & 5 · OWN 35`, `1st & Goal · OPP 8`, or just the parts that exist. */
export function situationText(stats: Record<string, unknown>): string {
  const down = num(stats.down);
  const los = num(stats.line_of_scrimmage);
  const distance = distanceOf(stats);
  const parts: string[] = [];
  if (down !== null) {
    const isGoal = los !== null && distance !== null && los + distance >= FIELD_BOUNDS.yardLineMax;
    parts.push(
      distance === null
        ? ordinalDown(down)
        : `${ordinalDown(down)} & ${isGoal ? 'Goal' : String(distance)}`
    );
  }
  if (los !== null) parts.push(formatYardLine(los));
  return parts.length === 0 ? NOT_RECORDED : parts.join(' · ');
}

/** Seconds remaining in the quarter, from `MM:SS`. */
export function clockSeconds(clock: unknown): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(str(clock));
  if (match === null) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

/** `Q2 7:32`. */
export function timeText(stats: Record<string, unknown>): string {
  const quarter = num(stats.quarter);
  const clock = str(stats.game_clock).replace(/^0(\d:)/, '$1');
  const q =
    quarter === null
      ? ''
      : quarter > FIELD_BOUNDS.regulationQuarterMax
        ? `OT${quarter - 4}`
        : `Q${quarter}`;
  const text = [q, clock].filter((part) => part !== '').join(' ');
  return text === '' ? NOT_RECORDED : text;
}

/** Chronological key: quarter, then clock counting DOWN. */
function timeSortValue(stats: Record<string, unknown>): number | null {
  const quarter = num(stats.quarter);
  if (quarter === null) return null;
  const seconds = clockSeconds(stats.game_clock);
  return quarter * 10_000 + (seconds === null ? 0 : 1_000 - seconds);
}

export function gainText(stats: Record<string, unknown>): string {
  const gain = num(stats.gain_loss);
  if (gain === null) return NOT_RECORDED;
  return gain > 0 ? `+${gain}` : String(gain);
}

const PLAY_TYPE_OPTIONS: readonly ValueOption[] = (() => {
  const seen = new Map<string, string>();
  for (const phase of PLAY_PHASES) {
    for (const option of PLAY_TYPES_BY_PHASE[phase]) {
      if (!seen.has(option.value))
        seen.set(option.value, option.label.replace(/ \(opponent\)$/, ''));
    }
  }
  return [...seen.entries()].map(([value, label]) => ({ value, label }));
})();

function labelIn(options: readonly ValueOption[]): (value: string) => string {
  return (value) =>
    value === BLANK_VALUE ? BLANK_LABEL : (options.find((o) => o.value === value)?.label ?? value);
}

export const playTypeLabel = labelIn(PLAY_TYPE_OPTIONS);
const specialtyLabel = labelIn(SPECIALTY_OUTCOME_OPTIONS);

/** Compact outcome words for the table (the long form is the filter label). */
const OUTCOME_SHORT: Readonly<Record<string, string>> = {
  na: '',
  complete: 'Complete',
  incomplete: 'Incomplete',
  drop: 'Drop',
  penalty: 'Penalty',
  sack: 'Sack',
  tfl: 'TFL',
  no_play: 'No play',
};

export function outcomeText(stats: Record<string, unknown>): string {
  const outcome = str(stats.play_outcome);
  if (outcome === '') return NOT_RECORDED;
  const short = OUTCOME_SHORT[outcome];
  if (short === undefined) return PLAY_OUTCOME_LABELS[outcome] ?? outcome;
  return short === '' ? NOT_RECORDED : short;
}

/** `Power Right · #62`, `Power Right`, `#62` or a dash. */
export function playcallText(stats: Record<string, unknown>): string {
  const name = str(stats.play_name);
  const book = str(stats.playbook_number);
  const parts = [name, book === '' ? '' : `#${book}`].filter((part) => part !== '');
  return parts.length === 0 ? NOT_RECORDED : parts.join(' · ');
}

/** Table-width names for the specialty tags (the full label is the filter / edit label). */
const SPECIALTY_SHORT: Readonly<Record<string, string>> = {
  touchdown: 'TD',
  interception: 'INT',
  fumble: 'Fumble',
  fumble_recovered: 'Fum. rec.',
  field_goal_make: 'FG ✓',
  field_goal_miss: 'FG ✕',
  extra_point_make: 'XP ✓',
  extra_point_miss: 'XP ✕',
  two_point_conversion: '2-pt ✓',
  two_point_failed: '2-pt ✕',
  defensive_two_point: 'DEF 2-pt',
  kick_blocked: 'Kick blk',
  onside_recovered: 'Onside rec.',
  safety: 'Safety',
  touchback: 'Touchback',
  turnover_on_downs: 'TOD',
  defensive_touchdown: 'DEF TD',
  punt_block: 'Punt blk',
};

/**
 * The one-tap penalty tags as selectable values (archive edit dialog, Tags
 * filter labels). The tag names the unit that fouled.
 */
export const PENALTY_TAG_OPTIONS: readonly ValueOption[] = [
  { value: OFFENSIVE_PENALTY_TAG, label: 'Offensive Penalty' },
  { value: DEFENSIVE_PENALTY_TAG, label: 'Defensive Penalty' },
];
const PENALTY_TAG_VALUES: readonly string[] = PENALTY_TAG_OPTIONS.map((option) => option.value);

/** Table-width names for the penalty tags. */
const PENALTY_TAG_SHORT: Readonly<Record<string, string>> = {
  [OFFENSIVE_PENALTY_TAG]: 'OFF penalty',
  [DEFENSIVE_PENALTY_TAG]: 'DEF penalty',
};
const penaltyTagLabel = labelIn(PENALTY_TAG_OPTIONS);

/** A free tag for display: penalty tags get their human name, the rest stay verbatim. */
function freeTagLabel(tag: string, form: 'short' | 'long'): string {
  const lower = tag.toLowerCase();
  if (!PENALTY_TAG_VALUES.includes(lower)) return tag;
  return form === 'short' ? (PENALTY_TAG_SHORT[lower] ?? tag) : penaltyTagLabel(lower);
}

/** Label for any Tags-facet value: specialty outcome, penalty tag or free tag. */
export function tagFacetLabel(value: string): string {
  const lower = value.toLowerCase();
  if (PENALTY_TAG_VALUES.includes(lower)) return penaltyTagLabel(lower);
  return specialtyLabel(value);
}

/** Specialty tags (TD, INT …) then free tags — compact, for cells and cards. */
export function tagLabels(stats: Record<string, unknown>): string[] {
  return [
    ...list(stats.specialty_outcome).map(
      (value) => SPECIALTY_SHORT[value] ?? specialtyLabel(value)
    ),
    ...list(stats.tags).map((tag) => freeTagLabel(tag, 'short')),
  ];
}

/** Specialty tags then free tags, with full names (expander, tooltips). */
export function tagLabelsLong(stats: Record<string, unknown>): string[] {
  return [
    ...list(stats.specialty_outcome).map(specialtyLabel),
    ...list(stats.tags).map((tag) => freeTagLabel(tag, 'long')),
  ];
}

function scalarValues(field: string): (play: PlayView) => string[] {
  return (play) => {
    const value = str(play.stats[field]);
    return value === '' ? [] : [value];
  };
}

function numberOf(field: string): (play: PlayView) => number | null {
  return (play) => num(play.stats[field]);
}

/* ---------------------------------------------------------- fixed columns */

const ALL_PHASES = PLAY_PHASES;

const DESIGNED_TYPE_OPTIONS: readonly ValueOption[] = CONCEPT_DESIGN_TYPES.map((value) => ({
  value,
  label: CONCEPT_DESIGN_LABELS[value],
}));
const ST_ONLY: readonly PlayPhase[] = ['special_teams'];
const same = (label: string) => (): string => label;

const PHASE_OPTIONS: readonly ValueOption[] = PLAY_PHASES.map((phase) => ({
  value: phase,
  label: PHASE_LABELS[phase],
}));

const DOWN_OPTIONS: readonly ValueOption[] = [1, 2, 3, 4].map((down) => ({
  value: String(down),
  label: ordinalDown(down),
}));

const QUARTER_OPTIONS: readonly ValueOption[] = Array.from(
  { length: FIELD_BOUNDS.quarterMax },
  (_, index) => ({
    value: String(index + 1),
    label: index < 4 ? `Q${index + 1}` : `OT${index - 3}`,
  })
);

const STARRED_OPTIONS: readonly ValueOption[] = [
  { value: 'true', label: 'Starred' },
  { value: 'false', label: 'Not starred' },
];

/** Facet value for a play the team rule could not score (`success_measure` null). */
export const NOT_MEASURED_VALUE = 'not_measured';

const SUCCESS_OPTIONS: readonly ValueOption[] = [
  { value: 'true', label: 'Success' },
  { value: 'false', label: 'Not success' },
  { value: NOT_MEASURED_VALUE, label: 'Not measured' },
];

const KICK_DISTANCE_TYPES: readonly string[] = ['punt', 'kickoff'];
const RETURN_TYPES: readonly string[] = ['punt_return', 'kick_return'];
const MAKE_MISS_TYPES: readonly string[] = ['field_goal', 'extra_point', 'fg_block', 'xp_block'];
const MAKE_MISS_TAGS: readonly string[] = [
  'field_goal_make',
  'field_goal_miss',
  'extra_point_make',
  'extra_point_miss',
];

/**
 * Why a play has no Success Measure — the Success cell's tooltip and the row's
 * neutral (untinted) look. A run / pass with a down and a gain/loss is always
 * scored (a 3rd/4th down without the spot falls back to the team's late-down
 * yardage), so only genuinely unmeasurable snaps land here.
 */
export function notMeasuredReason(play: PlayView): string {
  const stats = play.stats;
  const playType = str(stats.play_type);
  if (playType === 'other') return 'Not measured: play type Other has no success rule.';
  if (playType === 'run' || playType === 'pass') {
    if (num(stats.down) === null) return 'Not measured: no down was logged.';
    if (num(stats.gain_loss) === null) return 'Not measured: no gain / loss was logged.';
  }
  if (KICK_DISTANCE_TYPES.includes(playType) && num(stats.kick_distance) === null) {
    return 'Not measured: no kick distance was logged.';
  }
  if (RETURN_TYPES.includes(playType) && num(stats.return_yards) === null) {
    return 'Not measured: no return yards were logged.';
  }
  if (
    MAKE_MISS_TYPES.includes(playType) &&
    !list(stats.specialty_outcome).some((tag) => MAKE_MISS_TAGS.includes(tag))
  ) {
    return 'Not measured: no made / missed tag was logged.';
  }
  return 'Not measured: the team switched this measure off, or the play predates the rule — edit it to re-score.';
}

const OUTCOME_OPTIONS: readonly ValueOption[] = Object.entries(PLAY_OUTCOME_LABELS).map(
  ([value, label]) => ({ value, label })
);

const DIRECTION_OPTIONS: readonly ValueOption[] = PLAY_DIRECTIONS.map((value) => ({
  value,
  label: PLAY_DIRECTION_LABELS[value],
}));

function fixedColumns(): ArchiveColumn[] {
  return [
    {
      id: 'play_number',
      header: '#',
      title: 'Play # and unit',
      group: 'meta',
      priority: 0,
      // Star + caret + number + unit badge.
      minWidth: 84,
      grow: 0,
      align: 'start',
      defaultVisible: true,
      isPinned: true,
      cell: 'play',
      text: (play) => `#${play.play_number}`,
      sortValue: (play) => play.play_number,
      facets: [
        {
          id: 'play_number',
          label: 'Play #',
          kind: 'range',
          number: (play) => play.play_number,
          min: 1,
        },
      ],
      editFields: [],
    },
    {
      // The star itself rides in the Play # cell (always visible); this column
      // is for sorting / filtering by it and for exports ("Starred" column).
      id: 'is_favorited',
      header: '★',
      title: 'Starred',
      group: 'meta',
      priority: 5,
      minWidth: 36,
      grow: 0,
      align: 'center',
      defaultVisible: false,
      cell: 'text',
      text: (play) => (play.is_favorited === true ? 'Starred' : NOT_RECORDED),
      sortValue: (play) => (play.is_favorited === true ? 1 : 0),
      facets: [
        {
          id: 'starred',
          label: 'Starred',
          kind: 'enum',
          values: (play) => [String(play.is_favorited === true)],
          fixedOptions: STARRED_OPTIONS,
          optionLabel: labelIn(STARRED_OPTIONS),
        },
      ],
      // Toggled with the star, never through the edit dialog.
      editFields: [],
    },
    {
      id: 'possession',
      header: 'Unit',
      title: 'Unit (offense / defense / special teams)',
      group: 'situation',
      priority: 4,
      minWidth: 44,
      grow: 0,
      align: 'center',
      // The unit badge already rides in the Play # cell; this column exists
      // for sorting / the picker.
      defaultVisible: false,
      cell: 'phase',
      text: (play) => {
        const phase = phaseOf(play);
        return phase === null ? NOT_RECORDED : PHASE_SHORT_LABELS[phase];
      },
      sortValue: (play) => {
        const phase = phaseOf(play);
        return phase === null ? null : PLAY_PHASES.indexOf(phase);
      },
      // Filtered through the toolbar's O/D/ST control (the `unit` param).
      facets: [
        {
          id: 'unit',
          label: 'Unit',
          kind: 'enum',
          values: scalarValues('possession'),
          fixedOptions: PHASE_OPTIONS,
          optionLabel: labelIn(PHASE_OPTIONS),
        },
      ],
      editFields: [
        {
          name: 'possession',
          kind: 'select',
          section: 'situation',
          phases: ALL_PHASES,
          label: same('Unit'),
          options: () => PHASE_OPTIONS,
        },
      ],
    },
    {
      id: 'time',
      header: 'Time',
      title: 'Quarter and game clock',
      group: 'situation',
      priority: 6,
      minWidth: 68,
      maxWidth: 90,
      grow: 0,
      align: 'start',
      defaultVisible: true,
      cell: 'text',
      text: (play) => timeText(play.stats),
      sortValue: (play) => timeSortValue(play.stats),
      facets: [
        {
          id: 'quarter',
          label: 'Quarter',
          kind: 'enum',
          values: scalarValues('quarter'),
          fixedOptions: QUARTER_OPTIONS.slice(0, FIELD_BOUNDS.regulationQuarterMax),
          optionLabel: labelIn(QUARTER_OPTIONS),
        },
      ],
      editFields: [
        {
          name: 'quarter',
          kind: 'select',
          section: 'situation',
          phases: ALL_PHASES,
          label: same('Quarter'),
          options: () => QUARTER_OPTIONS,
        },
        {
          name: 'game_clock',
          kind: 'text',
          section: 'situation',
          phases: ALL_PHASES,
          label: same('Game clock'),
          placeholder: 'mm:ss',
        },
      ],
    },
    {
      id: 'situation',
      header: 'Down & distance',
      title: 'Down, distance and line of scrimmage',
      group: 'situation',
      priority: 3,
      minWidth: 126,
      maxWidth: 170,
      grow: 1,
      align: 'start',
      defaultVisible: true,
      cell: 'situation',
      text: (play) => situationText(play.stats),
      sortValue: (play) => {
        const down = num(play.stats.down);
        if (down === null) return null;
        return down * 1000 + (distanceOf(play.stats) ?? 0);
      },
      facets: [
        {
          id: 'down',
          label: 'Down',
          kind: 'enum',
          values: scalarValues('down'),
          fixedOptions: DOWN_OPTIONS,
          optionLabel: labelIn(DOWN_OPTIONS),
        },
        {
          id: 'distance',
          label: 'Yards to go',
          kind: 'range',
          number: (play) => distanceOf(play.stats),
          min: 0,
          max: 99,
        },
        {
          id: 'yard_line',
          label: 'Line of scrimmage (1 = own goal, 99 = opponent goal)',
          kind: 'range',
          number: numberOf('line_of_scrimmage'),
          min: 1,
          max: 99,
        },
      ],
      editFields: [
        {
          name: 'down',
          kind: 'select',
          section: 'situation',
          phases: ALL_PHASES,
          label: same('Down'),
          options: () => DOWN_OPTIONS,
        },
        {
          name: 'line_of_scrimmage',
          kind: 'number',
          section: 'situation',
          phases: ALL_PHASES,
          label: same('Line of scrimmage'),
          hint: '1–99 toward the opponent’s goal',
          min: 1,
          max: 99,
        },
        {
          name: 'first_down_line',
          kind: 'number',
          section: 'situation',
          phases: ALL_PHASES,
          label: same('First-down line'),
          min: 1,
          max: 99,
        },
      ],
    },
    {
      id: 'hash',
      header: 'Hash',
      title: 'Hash / side',
      group: 'situation',
      priority: 48,
      minWidth: 58,
      grow: 0,
      align: 'start',
      defaultVisible: false,
      cell: 'text',
      text: (play) => labelIn(HASH_OPTIONS)(str(play.stats.hash)) || NOT_RECORDED,
      sortValue: (play) => str(play.stats.hash) || null,
      facets: [
        {
          id: 'hash',
          label: 'Hash / side',
          kind: 'enum',
          values: scalarValues('hash'),
          fixedOptions: HASH_OPTIONS,
          optionLabel: labelIn(HASH_OPTIONS),
        },
      ],
      editFields: [
        {
          name: 'hash',
          kind: 'select',
          section: 'situation',
          phases: ALL_PHASES,
          label: same('Hash / side'),
          options: () => HASH_OPTIONS,
        },
      ],
    },
    {
      id: 'play_type',
      header: 'Type',
      title: 'Play type',
      group: 'call',
      priority: 9,
      minWidth: 56,
      maxWidth: 96,
      grow: 0,
      align: 'start',
      defaultVisible: true,
      cell: 'text',
      text: (play) => {
        const value = str(play.stats.play_type);
        return value === '' ? NOT_RECORDED : playTypeLabel(value);
      },
      sortValue: (play) => str(play.stats.play_type) || null,
      facets: [
        {
          id: 'play_type',
          label: 'Play type',
          kind: 'enum',
          values: scalarValues('play_type'),
          fixedOptions: PLAY_TYPE_OPTIONS,
          optionLabel: playTypeLabel,
        },
      ],
      editFields: [
        {
          name: 'play_type',
          kind: 'select',
          section: 'call',
          phases: ALL_PHASES,
          label: same('Play type'),
          options: (phase) => PLAY_TYPES_BY_PHASE[phase],
        },
      ],
    },
    {
      // Read-only: the server works it out from the play's offensive
      // concept(s) when the play is saved; old plays stay blank.
      id: DESIGNED_PLAY_TYPE_FIELD,
      header: 'Designed',
      title: 'Designed type',
      group: 'call',
      priority: 26,
      minWidth: 60,
      maxWidth: 80,
      grow: 0,
      align: 'start',
      defaultVisible: false,
      phases: ['offense', 'defense'],
      cell: 'text',
      text: (play) => {
        const value = str(play.stats[DESIGNED_PLAY_TYPE_FIELD]).toLowerCase();
        return isConceptDesignType(value) ? CONCEPT_DESIGN_LABELS[value] : NOT_RECORDED;
      },
      sortValue: (play) => {
        const value = str(play.stats[DESIGNED_PLAY_TYPE_FIELD]).toLowerCase();
        return isConceptDesignType(value) ? CONCEPT_DESIGN_TYPES.indexOf(value) : null;
      },
      facets: [
        {
          id: DESIGNED_PLAY_TYPE_FIELD,
          label: 'Designed type',
          kind: 'enum',
          values: (play) => {
            const value = str(play.stats[DESIGNED_PLAY_TYPE_FIELD]).toLowerCase();
            return isConceptDesignType(value) ? [value] : [];
          },
          fixedOptions: DESIGNED_TYPE_OPTIONS,
          optionLabel: labelIn(DESIGNED_TYPE_OPTIONS),
        },
      ],
      editFields: [],
    },
    {
      id: 'playcall',
      header: 'Playcall',
      title: 'Playcall (name and playbook #)',
      group: 'call',
      priority: 11,
      minWidth: 96,
      maxWidth: 170,
      grow: 2,
      align: 'start',
      defaultVisible: true,
      cell: 'text',
      text: (play) => playcallText(play.stats),
      sortValue: (play) => playcallText(play.stats).toLowerCase(),
      facets: [
        {
          id: 'playcall',
          label: 'Playcall',
          kind: 'text',
          text: (play) => `${str(play.stats.play_name)} ${str(play.stats.playbook_number)}`,
        },
      ],
      editFields: [
        {
          name: 'play_name',
          kind: 'text',
          section: 'call',
          phases: ALL_PHASES,
          label: same('Playcall name'),
        },
        {
          name: 'playbook_number',
          kind: 'text',
          section: 'call',
          phases: ALL_PHASES,
          label: same('Playcall #'),
        },
      ],
    },
  ];
}

function resultColumns(): ArchiveColumn[] {
  return [
    {
      id: 'play_direction',
      header: 'Dir.',
      title: 'Play direction',
      group: 'result',
      priority: 52,
      minWidth: 56,
      grow: 0,
      align: 'start',
      defaultVisible: false,
      cell: 'text',
      text: (play) => labelIn(DIRECTION_OPTIONS)(str(play.stats.play_direction)) || NOT_RECORDED,
      sortValue: (play) => str(play.stats.play_direction) || null,
      facets: [
        {
          id: 'play_direction',
          label: 'Play direction',
          kind: 'enum',
          values: scalarValues('play_direction'),
          fixedOptions: DIRECTION_OPTIONS,
          optionLabel: labelIn(DIRECTION_OPTIONS),
        },
      ],
      editFields: [
        {
          name: 'play_direction',
          kind: 'select',
          section: 'result',
          phases: ALL_PHASES,
          label: same('Play direction'),
          options: () => DIRECTION_OPTIONS,
        },
      ],
    },
    {
      id: 'gain_loss',
      header: 'Gain',
      title: 'Gain / loss (yards)',
      group: 'result',
      priority: 2,
      minWidth: 56,
      grow: 0,
      align: 'end',
      defaultVisible: true,
      cell: 'gain',
      text: (play) => gainText(play.stats),
      sortValue: (play) => num(play.stats.gain_loss),
      facets: [
        {
          id: 'gain_loss',
          label: 'Gain / loss (yards)',
          kind: 'range',
          number: numberOf('gain_loss'),
          min: -99,
          max: 99,
        },
      ],
      editFields: [
        {
          name: 'gain_loss',
          kind: 'number',
          section: 'result',
          phases: ALL_PHASES,
          label: same('Gain / loss (yards)'),
          min: -99,
          max: 99,
        },
      ],
    },
    {
      id: 'play_outcome',
      header: 'Result',
      title: 'Play outcome',
      group: 'result',
      priority: 12,
      minWidth: 78,
      maxWidth: 100,
      grow: 0,
      align: 'start',
      defaultVisible: true,
      cell: 'text',
      text: (play) => outcomeText(play.stats),
      sortValue: (play) => str(play.stats.play_outcome) || null,
      facets: [
        {
          id: 'play_outcome',
          label: 'Play outcome',
          kind: 'enum',
          values: scalarValues('play_outcome'),
          fixedOptions: OUTCOME_OPTIONS,
          optionLabel: labelIn(OUTCOME_OPTIONS),
        },
      ],
      editFields: [
        {
          name: 'play_outcome',
          kind: 'select',
          section: 'result',
          phases: ALL_PHASES,
          label: same('Outcome'),
          // "N/A (run or clean play)" clips in a dialog select; same value, shorter words.
          options: (_phase, draft) =>
            outcomeOptionsFor(draft.play_type ?? '').map((option) =>
              option.value === 'na' ? { ...option, label: 'None (clean play)' } : option
            ),
        },
      ],
    },
    {
      id: 'kick_distance',
      header: 'Kick',
      title: 'Kick distance (yards)',
      group: 'result',
      priority: 44,
      minWidth: 52,
      grow: 0,
      align: 'end',
      defaultVisible: false,
      phases: ST_ONLY,
      cell: 'text',
      text: (play) => str(play.stats.kick_distance) || NOT_RECORDED,
      sortValue: numberOf('kick_distance'),
      facets: [
        {
          id: 'kick_distance',
          label: 'Kick distance',
          kind: 'range',
          number: numberOf('kick_distance'),
          min: 0,
          max: 99,
        },
      ],
      editFields: [
        {
          name: 'kick_distance',
          kind: 'number',
          section: 'result',
          phases: ST_ONLY,
          label: same('Kick distance (yards)'),
          min: 0,
          max: 99,
        },
      ],
    },
    {
      id: 'return_yards',
      header: 'Ret.',
      title: 'Return yards',
      group: 'result',
      priority: 45,
      minWidth: 52,
      grow: 0,
      align: 'end',
      defaultVisible: false,
      phases: ST_ONLY,
      cell: 'text',
      text: (play) => str(play.stats.return_yards) || NOT_RECORDED,
      sortValue: numberOf('return_yards'),
      facets: [
        {
          id: 'return_yards',
          label: 'Return yards',
          kind: 'range',
          number: numberOf('return_yards'),
          min: -20,
          max: 109,
        },
      ],
      editFields: [
        {
          name: 'return_yards',
          kind: 'number',
          section: 'result',
          phases: ST_ONLY,
          label: same('Return yards'),
          min: -20,
          max: 109,
        },
      ],
    },
    {
      id: 'tags',
      header: 'Tags',
      title: 'Tags (touchdown, turnover … and custom tags)',
      group: 'tags',
      priority: 21,
      minWidth: 96,
      maxWidth: 170,
      grow: 1,
      align: 'start',
      defaultVisible: true,
      cell: 'tags',
      text: (play) => tagLabelsLong(play.stats).join(', ') || NOT_RECORDED,
      sortValue: (play) => tagLabelsLong(play.stats).join(', ').toLowerCase() || null,
      facets: [
        {
          id: 'tags',
          label: 'Tags',
          kind: 'enum',
          values: (play) => [
            ...list(play.stats.specialty_outcome),
            ...list(play.stats.tags).map((tag) =>
              PENALTY_TAG_VALUES.includes(tag.toLowerCase()) ? tag.toLowerCase() : tag
            ),
          ],
          optionLabel: tagFacetLabel,
          // Every specialty outcome (incl. Defensive Touchdown) and both penalty
          // tags are always selectable; free-form tags are discovered from plays.
          fixedOptions: [...SPECIALTY_OUTCOME_OPTIONS, ...PENALTY_TAG_OPTIONS],
        },
      ],
      editFields: [
        {
          name: 'specialty_outcome',
          kind: 'multi',
          section: 'tags',
          phases: ALL_PHASES,
          label: same('Tags'),
          options: () => SPECIALTY_OUTCOME_OPTIONS,
          span: 3,
        },
        {
          // Offensive / Defensive Penalty chips — stored in `tags[]`.
          name: 'penalty_tags',
          kind: 'multi',
          section: 'tags',
          phases: ALL_PHASES,
          label: same('Penalty'),
          options: () => PENALTY_TAG_OPTIONS,
          storedInTags: true,
          span: 3,
        },
        {
          name: 'tags',
          kind: 'list',
          section: 'tags',
          phases: ALL_PHASES,
          label: same('Custom tags'),
          hint: 'Comma-separated, e.g. red-zone, play-action',
          span: 3,
        },
      ],
    },
    {
      id: 'success_measure',
      header: 'Succ.',
      title: 'Success (team rule)',
      group: 'result',
      priority: 40,
      minWidth: 50,
      grow: 0,
      align: 'center',
      defaultVisible: true,
      cell: 'success',
      text: (play) =>
        play.success_measure === null
          ? notMeasuredReason(play)
          : play.success_measure
            ? 'Yes'
            : 'No',
      sortValue: (play) => (play.success_measure === null ? null : play.success_measure ? 1 : 0),
      facets: [
        {
          id: 'success',
          label: 'Success',
          kind: 'enum',
          values: (play) =>
            play.success_measure === null ? [NOT_MEASURED_VALUE] : [String(play.success_measure)],
          fixedOptions: SUCCESS_OPTIONS,
          optionLabel: labelIn(SUCCESS_OPTIONS),
        },
      ],
      // Server-computed from the team's rule — never edited by hand.
      editFields: [],
    },
    {
      id: 'notes',
      header: 'Notes',
      title: 'Notes',
      group: 'tags',
      priority: 30,
      minWidth: 110,
      maxWidth: 180,
      grow: 3,
      align: 'start',
      defaultVisible: true,
      cell: 'text',
      text: (play) => str(play.stats.notes) || NOT_RECORDED,
      sortValue: (play) => str(play.stats.notes).toLowerCase() || null,
      facets: [
        { id: 'notes', label: 'Notes', kind: 'text', text: (play) => str(play.stats.notes) },
      ],
      editFields: [
        {
          name: 'notes',
          kind: 'textarea',
          section: 'tags',
          phases: ALL_PHASES,
          label: same('Notes'),
          span: 3,
        },
      ],
    },
    {
      id: 'recorded_at',
      header: 'Logged',
      title: 'Time logged',
      group: 'meta',
      priority: 60,
      minWidth: 70,
      grow: 0,
      align: 'start',
      defaultVisible: false,
      cell: 'text',
      text: (play) => {
        const at = new Date(play.recorded_at);
        return Number.isNaN(at.getTime())
          ? NOT_RECORDED
          : at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
      },
      sortValue: (play) => play.recorded_at,
      facets: [],
      editFields: [],
    },
  ];
}

/* --------------------------------------------------------- catalog columns */

const UNIT_PREFIX: Readonly<Record<PlayPhase, string>> = {
  offense: 'OFF',
  defense: 'DEF',
  special_teams: 'ST',
};

/**
 * Placement of the catalog stats we know about. A stat not listed here (added
 * to the catalog later) still gets a column — hidden by default, low priority,
 * offered in the picker — so nothing is ever dropped.
 */
const CATALOG_LAYOUT: Readonly<
  Record<string, { priority: number; minWidth: number; defaultVisible: boolean }>
> = {
  offensive_concept: { priority: 14, minWidth: 86, defaultVisible: true },
  offensive_formation: { priority: 15, minWidth: 76, defaultVisible: true },
  offensive_personnel: { priority: 34, minWidth: 60, defaultVisible: true },
  motion: { priority: 22, minWidth: 64, defaultVisible: true },
  protection: { priority: 38, minWidth: 72, defaultVisible: true },
  defensive_concept: { priority: 16, minWidth: 86, defaultVisible: true },
  defensive_front: { priority: 18, minWidth: 56, defaultVisible: true },
  defensive_coverage: { priority: 19, minWidth: 64, defaultVisible: true },
  blitz_call: { priority: 20, minWidth: 64, defaultVisible: true },
  st_concept: { priority: 24, minWidth: 80, defaultVisible: true },
  st_formation: { priority: 42, minWidth: 80, defaultVisible: false },
  st_personnel: { priority: 55, minWidth: 80, defaultVisible: false },
  st_protection: { priority: 56, minWidth: 80, defaultVisible: false },
  st_coverage_scheme: { priority: 57, minWidth: 84, defaultVisible: false },
  st_return_concept: { priority: 43, minWidth: 84, defaultVisible: false },
  st_rush_block_scheme: { priority: 58, minWidth: 84, defaultVisible: false },
  st_opponent_alignment: { priority: 59, minWidth: 84, defaultVisible: false },
};

const UNKNOWN_CATALOG_LAYOUT = { priority: 70, minWidth: 84, defaultVisible: false };

interface CatalogFieldInfo {
  field: string;
  category: StatCategory;
  cards: PlayEntryStatCardView[];
}

/** Catalog wire fields → the cards that write them, across phases, in card order. */
export function catalogFields(): CatalogFieldInfo[] {
  const byField = new Map<string, CatalogFieldInfo>();
  for (const phase of PLAY_PHASES) {
    for (const card of PLAY_ENTRY_STAT_CARDS[phase]) {
      const entry = byField.get(card.field) ?? {
        field: card.field,
        category: card.category,
        cards: [],
      };
      entry.cards.push(card);
      byField.set(card.field, entry);
    }
  }
  return [...byField.values()];
}

/** Shorter of an option's label and stored value — `Jet` over `Jet Motion`, `2` over `Cover2`. */
export function compactOptionText(value: string, label: string): string {
  return label.length <= value.length ? label : value;
}

function catalogColumn(info: CatalogFieldInfo, ctx: RegistryContext): ArchiveColumn {
  const definition = statDefinition(info.category);
  const layout = CATALOG_LAYOUT[info.field] ?? UNKNOWN_CATALOG_LAYOUT;
  const phases = info.cards.map((card) => card.phase as PlayPhase);
  const sideByPhase: Partial<Record<PlayPhase, StatSide>> = {};
  const cardByPhase = new Map<PlayPhase, PlayEntryStatCardView>();
  for (const card of info.cards) {
    sideByPhase[card.phase as PlayPhase] = card.side;
    cardByPhase.set(card.phase as PlayPhase, card);
  }
  const unit = definition.unit as PlayPhase;
  const header = definition.short_label;

  const allOptions = (): ValueOption[] => {
    const seen = new Map<string, string>();
    for (const side of ['own', 'opponent'] as const) {
      for (const option of ctx.optionsFor(info.category, side)) {
        if (!seen.has(option.value)) seen.set(option.value, option.label);
      }
    }
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  };
  const labelOf = (value: string): string => {
    if (value === BLANK_VALUE) return BLANK_LABEL;
    return allOptions().find((option) => option.value === value)?.label ?? value;
  };
  const valueOn = (play: PlayView): string => {
    const phase = phaseOf(play);
    if (phase === null || !phases.includes(phase)) return '';
    return str(play.stats[info.field]);
  };

  return {
    id: info.field,
    header,
    kicker: UNIT_PREFIX[unit],
    title: definition.label,
    group: sideByPhase[unit] === 'opponent' ? 'look' : 'call',
    priority: layout.priority,
    minWidth: layout.minWidth,
    maxWidth: 120,
    grow: 1,
    align: 'start',
    defaultVisible: layout.defaultVisible,
    cell: 'catalog',
    phases,
    sideByPhase,
    category: info.category,
    text: (play) => {
      const value = valueOn(play);
      return value === '' ? NOT_RECORDED : compactOptionText(value, labelOf(value));
    },
    sortValue: (play) => valueOn(play).toLowerCase() || null,
    facets: [
      {
        id: info.field,
        label: definition.label,
        kind: 'enum',
        values: (play) => {
          const value = valueOn(play);
          return value === '' ? [] : [value];
        },
        optionLabel: labelOf,
      },
    ],
    editFields: [
      {
        name: info.field,
        kind: 'select',
        section: 'call',
        phases,
        label: (phase) => {
          const card = cardByPhase.get(phase);
          if (card === undefined) return definition.label;
          // "Opponent's defensive front"; own side reads as the stat's name.
          return card.side === 'opponent' ? card.label : definition.label;
        },
        options: (phase) => {
          const side = sideByPhase[phase] ?? 'own';
          return ctx.optionsFor(info.category, side);
        },
      },
    ],
  };
}

/** The dialog section a catalog field lands in on this phase. */
export function editSectionFor(
  column: ArchiveColumn,
  field: EditField,
  phase: PlayPhase
): EditSection {
  if (column.sideByPhase === undefined) return field.section;
  return column.sideByPhase[phase] === 'opponent' ? 'look' : 'call';
}

/* ----------------------------------------------------------- custom stats */

function customColumn(field: StatFieldDefinition, index: number): ArchiveColumn {
  const read = (play: PlayView): unknown => {
    const bag = (play as { custom_stats?: unknown }).custom_stats;
    const fromView =
      typeof bag === 'object' && bag !== null
        ? (bag as Record<string, unknown>)[field.stat_key]
        : undefined;
    if (fromView !== undefined) return fromView;
    const nested = play.stats.custom_stats;
    return typeof nested === 'object' && nested !== null
      ? (nested as Record<string, unknown>)[field.stat_key]
      : undefined;
  };
  const withBag = (play: PlayView): PlayView =>
    ({ ...play, custom_stats: { [field.stat_key]: read(play) } }) as PlayView;
  const enumOptions: ValueOption[] =
    field.data_type === 'boolean'
      ? [
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ]
      : (field.enum_values ?? []).map((option) => ({ value: option.value, label: option.label }));
  const phases: readonly PlayPhase[] =
    field.applies_to === 'general' ? ALL_PHASES : [field.applies_to as PlayPhase];
  const facetId = `${CUSTOM_COLUMN_PREFIX}${field.stat_key}`;
  const title = field.is_enabled ? field.label : `${field.label} (retired)`;
  const facet: FilterFacet =
    field.data_type === 'numeric'
      ? { id: facetId, label: title, kind: 'range', number: (play) => num(read(play)) }
      : field.data_type === 'text'
        ? { id: facetId, label: title, kind: 'text', text: (play) => str(read(play)) }
        : {
            id: facetId,
            label: title,
            kind: 'enum',
            values: (play) => {
              const value = read(play);
              if (value === null || value === undefined || value === '') return [];
              return Array.isArray(value) ? value.map(String) : [String(value)];
            },
            fixedOptions: enumOptions.length > 0 ? enumOptions : undefined,
            optionLabel: labelIn(enumOptions),
          };

  return {
    id: facetId,
    header: field.label,
    title,
    group: 'custom',
    priority: 80 + index,
    minWidth: field.data_type === 'text' ? 100 : 72,
    grow: field.data_type === 'text' ? 2 : 0,
    align: field.data_type === 'numeric' ? 'end' : 'start',
    defaultVisible: false,
    cell: 'text',
    phases,
    customField: field,
    text: (play) => formatCustomStatValue(withBag(play), field),
    sortValue: (play) => {
      const value = read(play);
      if (field.data_type === 'numeric') return num(value);
      const text = str(value);
      return text === '' ? null : text.toLowerCase();
    },
    facets: [facet],
    // Retired fields keep their column (history) but are not offered for new values.
    editFields: [
      {
        name: facetId,
        customKey: field.stat_key,
        kind:
          field.data_type === 'numeric'
            ? 'number'
            : field.data_type === 'enum' || field.data_type === 'boolean'
              ? 'select'
              : field.data_type === 'tag_list'
                ? 'list'
                : 'text',
        section: 'custom',
        phases,
        label: () => title,
        options: () => enumOptions,
      },
    ],
  };
}

/* ---------------------------------------------------------------- registry */

/**
 * Every archive column, in default display order: situation → call → the
 * catalog stats (card order) → result → tags & notes → record → custom stats.
 */
export function buildArchiveColumns(ctx: RegistryContext): ArchiveColumn[] {
  const fixed = fixedColumns();
  const results = resultColumns();
  const catalog = catalogFields().map((info) => catalogColumn(info, ctx));
  const custom = ctx.customFields
    .filter((field) => !field.is_builtin)
    .slice()
    .sort((left, right) =>
      left.display_order === right.display_order
        ? left.label.localeCompare(right.label)
        : left.display_order - right.display_order
    )
    .map(customColumn);
  const [direction, ...rest] = results;
  return [...fixed, ...catalog, direction, ...rest, ...custom].filter(
    (column): column is ArchiveColumn => column !== undefined
  );
}

export function columnById(columns: readonly ArchiveColumn[]): ReadonlyMap<string, ArchiveColumn> {
  return new Map(columns.map((column) => [column.id, column]));
}

/** Every facet of every column, keyed by facet id. */
export function facetIndex(
  columns: readonly ArchiveColumn[]
): ReadonlyMap<string, { facet: FilterFacet; column: ArchiveColumn }> {
  const map = new Map<string, { facet: FilterFacet; column: ArchiveColumn }>();
  for (const column of columns) {
    for (const facet of column.facets) map.set(facet.id, { facet, column });
  }
  return map;
}

/** Does this column carry information on this play's phase? */
export function appliesTo(column: ArchiveColumn, play: PlayView): boolean {
  if (column.phases === undefined) return true;
  const phase = phaseOf(play);
  return phase !== null && column.phases.includes(phase);
}

/** Catalog cell on a phase where the value is the OPPONENT's call. */
export function isOpponentValue(column: ArchiveColumn, play: PlayView): boolean {
  const phase = phaseOf(play);
  return phase !== null && column.sideByPhase?.[phase] === 'opponent';
}
